/**
 * AIF-03 — Step 4: execute (§11.5).
 *
 * On entry the parent has already POSTed the import (→ importId). This step
 * polls GET status every ~750ms (via `useImportJobStatus` in the parent) and
 * renders a progress bar `inserted / toInsert` + a Cancel button while running.
 * Cancel POSTs the cancel op; partials remain — the UI says so. On a terminal
 * status it renders the ImportReport inline (story H2: no separate doc):
 * inserted, skipped-by-reason breakdown, soft-deleted, kept-converted, deduped,
 * and a cancelled flag.
 */
import { type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type { AiFindingsImportJobStatus } from "@aeriosiq/types";
import { CircleCheckIcon } from "@aeriosiq/ui/icons";

export interface Step4ExecuteProps {
  job: AiFindingsImportJobStatus | null;
  starting: boolean;
  startError: string | null;
  onCancel: () => void;
  cancelling: boolean;
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <TableRow>
      <TableCell sx={{ border: 0, py: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
      </TableCell>
      <TableCell align="right" sx={{ border: 0, py: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {value}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export function Step4Execute({
  job,
  starting,
  startError,
  onCancel,
  cancelling,
}: Step4ExecuteProps): ReactElement {
  if (startError) {
    return (
      <Alert severity="error" data-testid="execute-error">
        {startError}
      </Alert>
    );
  }

  if (starting || !job) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body2" gutterBottom>
          Starting import…
        </Typography>
        <LinearProgress aria-label="Starting import" />
      </Box>
    );
  }

  const { status, progress, report } = job;

  if (status === "running") {
    const pct =
      progress.toInsert > 0
        ? Math.round((progress.inserted / progress.toInsert) * 100)
        : 0;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
        <Typography variant="body1">{`Importing… ${String(
          progress.inserted,
        )} / ${String(progress.toInsert)}`}</Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label="Import progress"
          data-testid="import-progress"
        />
        <Typography variant="caption" color="text.secondary">
          Cancelling keeps already-inserted findings — incremental, no rollback.
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="warning"
            onClick={onCancel}
            disabled={cancelling}
          >
            {cancelling ? "Cancelling…" : "Cancel"}
          </Button>
        </Box>
      </Box>
    );
  }

  // Terminal — render the ImportReport inline.
  const wasCancelled = status === "cancelled" || (report?.cancelled ?? false);
  const skippedTotal =
    (report?.totals.skippedUnmatchedClass ?? 0) +
    (report?.totals.skippedNoImage ?? 0) +
    (report?.totals.skippedNoDims ?? 0) +
    (report?.totals.belowFloor ?? 0);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}
      data-testid="import-report"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {status === "done" && !report?.cancelled ? (
          <CircleCheckIcon color="success" />
        ) : null}
        <Typography variant="h6">
          {status === "failed"
            ? "Import failed"
            : wasCancelled
              ? "Import cancelled"
              : "Import complete"}
        </Typography>
        {wasCancelled && (
          <Chip
            label="cancelled"
            color="warning"
            size="small"
            data-testid="cancelled-chip"
          />
        )}
      </Box>

      {wasCancelled && (
        <Alert severity="info">
          Cancelled mid-run — the findings inserted so far remain (no rollback).
        </Alert>
      )}
      {status === "failed" && (
        <Alert severity="error">
          The import job failed. Some findings may have been inserted; re-run
          with a deletion scope to reconcile.
        </Alert>
      )}

      {report && (
        <Table size="small" aria-label="Import report">
          <TableBody>
            <StatRow label="Inserted" value={report.inserted} />
            <StatRow label="Skipped (total)" value={skippedTotal} />
            <StatRow
              label="  · unmatched class"
              value={report.totals.skippedUnmatchedClass}
            />
            <StatRow
              label="  · no image match"
              value={report.totals.skippedNoImage}
            />
            <StatRow
              label="  · missing pixel dims"
              value={report.totals.skippedNoDims}
            />
            <StatRow
              label="  · below threshold"
              value={report.totals.belowFloor}
            />
            <StatRow label="Soft-deleted" value={report.softDeleted} />
            <StatRow
              label="Kept (already converted)"
              value={report.convertedKept}
            />
            <StatRow label="Deduped (rejected)" value={report.dedupeRejected} />
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
